import React, { useState } from 'react';
import {
  FileText,
  CheckCircle,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Shield,
  GitBranch,
  List,
  Users,
  Settings,
  Loader2
} from 'lucide-react';
import type { DocumentAssessment } from '../api';

interface DocumentAssessmentCardProps {
  documentId: number;
  filename: string;
  assessment: DocumentAssessment | null;
  assessmentStatus: 'pending' | 'assessing' | 'completed' | 'failed';
  processingMode: 'preserve' | 'interpret';
  onModeChange: (mode: 'preserve' | 'interpret') => void;
  onContinue: () => void;
  isLoading?: boolean;
}

const formatTypeLabels: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  swimlane_process_map: {
    label: 'Swimlane Process Map',
    icon: <GitBranch className="w-4 h-4" />,
    color: 'text-blue-600 bg-blue-100'
  },
  narrative_sop: {
    label: 'Narrative SOP',
    icon: <List className="w-4 h-4" />,
    color: 'text-green-600 bg-green-100'
  },
  hybrid: {
    label: 'Hybrid Document',
    icon: <FileText className="w-4 h-4" />,
    color: 'text-yellow-600 bg-yellow-100'
  },
  unstructured: {
    label: 'Unstructured',
    icon: <FileText className="w-4 h-4" />,
    color: 'text-gray-600 bg-gray-100'
  }
};

const priorityColors: Record<string, string> = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  low: 'bg-blue-100 text-blue-700 border-blue-200'
};

const DocumentAssessmentCard: React.FC<DocumentAssessmentCardProps> = ({
  documentId,
  filename,
  assessment,
  assessmentStatus,
  processingMode,
  onModeChange,
  onContinue,
  isLoading = false
}) => {
  const [isGapsExpanded, setIsGapsExpanded] = useState(false);

  // Loading state
  if (assessmentStatus === 'assessing' || isLoading) {
    return (
      <div className="border rounded-lg p-6 bg-white shadow-sm">
        <div className="flex items-center space-x-3">
          <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
          <div>
            <h3 className="font-medium text-gray-900">{filename}</h3>
            <p className="text-sm text-gray-500">Analyzing document structure...</p>
          </div>
        </div>
        <div className="mt-4">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-blue-500 h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
          </div>
        </div>
      </div>
    );
  }

  // Failed state
  if (assessmentStatus === 'failed' || !assessment) {
    return (
      <div className="border border-red-200 rounded-lg p-6 bg-red-50">
        <div className="flex items-center space-x-3">
          <AlertTriangle className="w-6 h-6 text-red-500" />
          <div>
            <h3 className="font-medium text-gray-900">{filename}</h3>
            <p className="text-sm text-red-600">Assessment failed. Using default interpretation mode.</p>
          </div>
        </div>
      </div>
    );
  }

  const formatInfo = formatTypeLabels[assessment.format_type] || formatTypeLabels.unstructured;
  const gaps = assessment.completeness?.identified_gaps || [];
  const isPreserveRecommended = assessment.recommendation === 'preserve';

  return (
    <div className="border rounded-lg p-6 bg-white shadow-sm">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-gray-100 rounded-lg">
            <FileText className="w-5 h-5 text-gray-600" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">{filename}</h3>
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${formatInfo.color}`}>
              {formatInfo.icon}
              <span className="ml-1">{formatInfo.label}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Scores */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        {/* Structure Quality */}
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Structure Quality</span>
            <span className={`text-lg font-bold ${
              assessment.structure_quality.score >= 70 ? 'text-green-600' :
              assessment.structure_quality.score >= 40 ? 'text-yellow-600' : 'text-red-600'
            }`}>
              {assessment.structure_quality.score}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full ${
                assessment.structure_quality.score >= 70 ? 'bg-green-500' :
                assessment.structure_quality.score >= 40 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${assessment.structure_quality.score}%` }}
            ></div>
          </div>
          {/* Structure indicators */}
          <div className="mt-2 flex flex-wrap gap-1">
            {assessment.structure_quality.has_clear_sections && (
              <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">Sections</span>
            )}
            {assessment.structure_quality.has_numbered_steps && (
              <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">Steps</span>
            )}
            {assessment.structure_quality.has_stakeholder_definitions && (
              <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">Stakeholders</span>
            )}
            {assessment.structure_quality.has_decision_logic && (
              <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">Decisions</span>
            )}
          </div>
        </div>

        {/* Completeness */}
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Completeness</span>
            <span className={`text-lg font-bold ${
              assessment.completeness.score >= 70 ? 'text-green-600' :
              assessment.completeness.score >= 40 ? 'text-yellow-600' : 'text-red-600'
            }`}>
              {assessment.completeness.score}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full ${
                assessment.completeness.score >= 70 ? 'bg-green-500' :
                assessment.completeness.score >= 40 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${assessment.completeness.score}%` }}
            ></div>
          </div>
          {gaps.length > 0 && (
            <p className="mt-2 text-xs text-gray-500">
              {gaps.length} gap{gaps.length !== 1 ? 's' : ''} identified
            </p>
          )}
        </div>
      </div>

      {/* Recommendation */}
      <div className={`rounded-lg p-3 mb-4 ${
        isPreserveRecommended ? 'bg-green-50 border border-green-200' : 'bg-blue-50 border border-blue-200'
      }`}>
        <div className="flex items-center space-x-2">
          {isPreserveRecommended ? (
            <Shield className="w-5 h-5 text-green-600" />
          ) : (
            <Settings className="w-5 h-5 text-blue-600" />
          )}
          <span className={`font-medium ${isPreserveRecommended ? 'text-green-700' : 'text-blue-700'}`}>
            Recommended: {isPreserveRecommended ? 'Preserve Original' : 'Interpret & Restructure'}
          </span>
        </div>
        <p className="mt-1 text-sm text-gray-600">{assessment.recommendation_reason}</p>
      </div>

      {/* Mode Selection */}
      <div className="mb-4">
        <p className="text-sm font-medium text-gray-700 mb-2">Processing Mode:</p>
        <div className="flex space-x-3">
          <label className={`flex-1 flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
            processingMode === 'preserve'
              ? 'border-green-500 bg-green-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}>
            <input
              type="radio"
              name={`mode-${documentId}`}
              value="preserve"
              checked={processingMode === 'preserve'}
              onChange={() => onModeChange('preserve')}
              className="sr-only"
            />
            <Shield className={`w-5 h-5 mr-2 ${processingMode === 'preserve' ? 'text-green-600' : 'text-gray-400'}`} />
            <div>
              <span className={`font-medium ${processingMode === 'preserve' ? 'text-green-700' : 'text-gray-700'}`}>
                Preserve Original
              </span>
              <p className="text-xs text-gray-500">Keep structure, add enhancements only</p>
            </div>
          </label>

          <label className={`flex-1 flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
            processingMode === 'interpret'
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}>
            <input
              type="radio"
              name={`mode-${documentId}`}
              value="interpret"
              checked={processingMode === 'interpret'}
              onChange={() => onModeChange('interpret')}
              className="sr-only"
            />
            <Settings className={`w-5 h-5 mr-2 ${processingMode === 'interpret' ? 'text-blue-600' : 'text-gray-400'}`} />
            <div>
              <span className={`font-medium ${processingMode === 'interpret' ? 'text-blue-700' : 'text-gray-700'}`}>
                Interpret & Restructure
              </span>
              <p className="text-xs text-gray-500">Rebuild using Jarvis templates</p>
            </div>
          </label>
        </div>
      </div>

      {/* Identified Gaps (Expandable) */}
      {gaps.length > 0 && (
        <div className="border-t pt-4">
          <button
            onClick={() => setIsGapsExpanded(!isGapsExpanded)}
            className="w-full flex items-center justify-between text-sm font-medium text-gray-700 hover:text-gray-900"
          >
            <span className="flex items-center">
              <AlertTriangle className="w-4 h-4 mr-2 text-yellow-500" />
              Identified Gaps ({gaps.length})
            </span>
            {isGapsExpanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>

          {isGapsExpanded && (
            <div className="mt-3 space-y-2">
              {gaps.map((gap, index) => (
                <div key={gap.gap_id || index} className={`p-2 rounded border ${priorityColors[gap.priority]}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{gap.gap_type.replace(/_/g, ' ')}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${priorityColors[gap.priority]}`}>
                      {gap.priority}
                    </span>
                  </div>
                  <p className="text-xs mt-1">{gap.description}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Continue Button */}
      <div className="mt-4 pt-4 border-t">
        <button
          onClick={onContinue}
          className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
        >
          Continue with {processingMode === 'preserve' ? 'Preservation' : 'Interpretation'}
        </button>
      </div>
    </div>
  );
};

export default DocumentAssessmentCard;
